import type { SearchItemCommandAction, SearchItemNavigateAction, SearchProvider, SearchResult } from "../types.ts";
import { takeWhere } from "../searchUtil.ts";
import { softContains } from "../../../utils/stringUtil.ts";
import { createUniqueId } from "solid-js";
import { isNil } from "lodash-es";
import type CustomerId from "../../../state/CustomerId.ts";
import type { IconName } from "../../../components/icon/Icon.tsx";
import { Icon } from "../../../components/icon/Icon.tsx";
import LegacyPlatformService from "../../LegacyPlatformService.ts";
import type { TFunction } from "i18next";
import Text from "../../../components/text/Text.tsx";
import { directUserToSupport } from "../../../utils/supportUtil.ts";
import PermissionService from "../../PermissionService.ts";
import FeatureService from "../../FeatureService.ts";

type Page = {
  name: string;
  icon: IconName;
  action: SearchItemNavigateAction<string> | SearchItemCommandAction;
  canAccess?: (customerId: CustomerId) => boolean;
};

export class PageSearchProvider implements SearchProvider {
  readonly type = "PAGE";
  readonly pages: Page[];

  constructor(
    private readonly t: TFunction,
    private readonly customerId: CustomerId,
    private readonly maxResults: number,
  ) {
    const prefixWithCustomerPath = (path: string) => (customerId: CustomerId) => `/customers/${customerId}${path}`;
    const createLinkAction = (url: string) => ({ type: "navigate" as const, href: prefixWithCustomerPath(url) });

    const allPages: Page[] = [
      {
        name: t(($) => $.SIDEBAR_ITEM_DATA_SOURCES_LABEL),
        icon: "database",
        action: { type: "navigate", href: prefixWithCustomerPath("/data-sources") },
        canAccess: PermissionService.canManageDataSources,
      },
      {
        name: t(($) => $.SIDEBAR_ITEM_USERS_LABEL),
        icon: "user",
        action: createLinkAction("/users"),
        canAccess: PermissionService.canManagePermissions,
      },
      {
        name: t(($) => $.SIDEBAR_ITEM_GROUPS_LABEL),
        icon: "userGroup",
        action: createLinkAction("/groups"),
        canAccess: PermissionService.canManagePermissions,
      },
      {
        name: t(($) => $.SIDEBAR_ITEM_ROLES_LABEL),
        icon: "key",
        action: createLinkAction("/roles"),
        canAccess: PermissionService.canManagePermissions,
      },
      {
        name: t(($) => $.SETTINGS_ITEM_PROFILE_LABEL),
        icon: "user",
        action: createLinkAction("/settings/profile"),
      },
      {
        name: t(($) => $.SETTINGS_ITEM_COMPANY_INFORMATION_LABEL),
        icon: "officeBuilding",
        action: createLinkAction("/settings/company"),
        canAccess: PermissionService.canManageCompanyInformation,
      },
      {
        name: t(($) => $.SETTINGS_ITEM_BILLING_AND_PAYMENT_LABEL),
        icon: "creditCard",
        action: createLinkAction("/settings/billing"),
        canAccess: PermissionService.canManageBilling,
      },
      {
        name: t(($) => $.SETTINGS_ITEM_INVOICES_LABEL),
        icon: "documentText",
        action: createLinkAction("/settings/invoices"),
        canAccess: PermissionService.canManageBilling,
      },
      {
        name: t(($) => $.SETTINGS_ITEM_USAGE_LABEL),
        icon: "chartBar",
        action: createLinkAction("/settings/usage"),
        canAccess: PermissionService.canManageBilling,
      },
      // TODO - DEV-8317, add 'Upgrade'
      {
        name: t(($) => $.SETTINGS_ITEM_API_KEYS_LABEL),
        icon: "lockClosed",
        action: createLinkAction("/settings/api-keys"),
        canAccess: PermissionService.canManageApiKeys,
      },
      {
        name: t(($) => $.SETTINGS_ITEM_WEBHOOKS_LABEL),
        icon: "webhook",
        action: createLinkAction("/settings/webhooks"),
        canAccess: (customerId) =>
          PermissionService.canManageWebhooks(customerId) && FeatureService.hasFeature("WEBHOOKS"),
      },
      {
        name: t(($) => $.SETTINGS_ITEM_EMAIL_CONFIGURATION_LABEL),
        icon: "mail",
        action: createLinkAction("/settings/email-configuration"),
        canAccess: (customerId) =>
          PermissionService.canManageEmailConfiguration(customerId) && FeatureService.hasFeature("SENDING_DOMAIN"),
      },
      {
        name: t(($) => $.SETTINGS_ITEM_SERVICE_ACCOUNTS_LABEL),
        icon: "userCircle",
        action: createLinkAction("/settings/service-accounts"),
        canAccess: PermissionService.canManageServiceAccounts,
      },
      {
        name: t(($) => $.SETTINGS_ITEM_SSO_LABEL),
        icon: "key",
        action: createLinkAction("/settings/sso"),
        canAccess: (customerId) =>
          PermissionService.canManageSSO(customerId) && FeatureService.hasFeature("SINGLE_SIGN_ON"),
      },
      {
        name: t(($) => $.HELP_MENU_ACTIONS_NAVIGATE_TO_HELP_CENTER),
        icon: "questionMarkCircle",
        action: { type: "navigate", href: () => "https://helpcenter.moreapp.com" },
      },
      {
        name: t(($) => $.HELP_MENU_ACTIONS_CONTACT_SUPPORT),
        icon: "chatAlt_2",
        action: { type: "command", run: async () => directUserToSupport() },
      },
      {
        name: t(($) => $.HELP_MENU_ACTIONS_SUPPORT_TICKETS),
        icon: "support",
        action: { type: "navigate", href: () => "https://meet.moreapp.com/support-tickets" },
      },
      {
        name: t(($) => $.HELP_MENU_ACTIONS_NAVIGATE_TO_DEVELOPER_DOCS),
        icon: "code",
        action: { type: "navigate", href: () => "https://docs.moreapp.com/docs/developer-docs" },
      },
      {
        name: t(($) => $.HELP_MENU_ACTIONS_NAVIGATE_TO_FEEDBACK_FORM),
        icon: "annotation",
        action: { type: "navigate", href: () => "https://links.moreapp.com/platform-feedback" },
      },
      {
        name: t(($) => $.HELP_MENU_ACTIONS_SWITCH_TO_OLD_PLATFORM),
        icon: "desktopComputer",
        action: {
          type: "navigate",
          href: (customerId: CustomerId) => LegacyPlatformService.createLegacyPlatformOverviewUrl(customerId),
        },
      },
    ];

    this.pages = allPages.filter((page) => isNil(page.canAccess) || page.canAccess(customerId));
  }

  async search(query: string): Promise<SearchResult[]> {
    return Array.from(
      takeWhere(this.pages, (page) => softContains(page.name, query), this.maxResults).map((page) => ({
        type: this.type,
        id: createUniqueId(),
        ContentComponent: () => (
          <div class="flex items-center gap-3 px-3 py-2.5">
            <div class="p-2 icon-tertiary">
              <Icon name={page.icon} type="solid" size="md" />
            </div>
            <Text class="text-base-medium text-primary" text={page.name} truncate />
          </div>
        ),
        actions: {
          Enter: page.action,
        },
      })),
    );
  }
}
